/**
 * Password Validation Utility
 * 
 * Provides strong password validation to prevent weak passwords
 * and improve account security.
 */

/**
 * Password requirements configuration
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialChars: '!@#$%^&*(),.?":{}|<>_-+=[]\\;\'`~',
};

/**
 * Validates password strength and returns detailed feedback
 * 
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with { isValid, errors, strength }
 * 
 * @example
 * const result = validatePassword('MyP@ssw0rd');
 * if (!result.isValid) {
 *   console.log(result.errors); // Array of error messages
 * }
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password || typeof password !== 'string') {
    return {
      isValid: false,
      errors: ['Password is required'],
      strength: 'none',
    };
  }

  // Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }

  // Check for uppercase letters
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter (A-Z)');
  }

  // Check for lowercase letters
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter (a-z)');
  }

  // Check for numbers
  if (PASSWORD_REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number (0-9)');
  }

  // Check for special characters
  if (PASSWORD_REQUIREMENTS.requireSpecialChars) {
    const specialCharRegex = new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`);
    if (!specialCharRegex.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*...)');
    }
  }

  // Calculate password strength
  const strength = calculatePasswordStrength(password);

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  };
};

/**
 * Calculates password strength score
 * 
 * @param {string} password - The password to evaluate
 * @returns {string} - Strength level: 'weak', 'fair', 'good', 'strong', 'very-strong'
 */
export const calculatePasswordStrength = (password) => {
  if (!password) return 'none';

  let score = 0;

  // Length scoring
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;

  // Character variety scoring
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  // Complexity scoring
  if (/[a-z].*[a-z]/.test(password)) score += 0.5; // Multiple lowercase
  if (/[A-Z].*[A-Z]/.test(password)) score += 0.5; // Multiple uppercase
  if (/\d.*\d/.test(password)) score += 0.5; // Multiple numbers
  if (/[^a-zA-Z0-9].*[^a-zA-Z0-9]/.test(password)) score += 0.5; // Multiple special chars

  // Return strength level based on score
  if (score < 4) return 'weak';
  if (score < 6) return 'fair';
  if (score < 8) return 'good';
  if (score < 10) return 'strong';
  return 'very-strong';
};

/**
 * Get user-friendly feedback for password strength
 * 
 * @param {string} strength - Strength level from calculatePasswordStrength
 * @returns {Object} - { color, text, percentage }
 */
export const getPasswordStrengthFeedback = (strength) => {
  const feedback = {
    'none': { color: '#ccc', text: 'No password', percentage: 0 },
    'weak': { color: '#e74c3c', text: 'Weak', percentage: 25 },
    'fair': { color: '#f39c12', text: 'Fair', percentage: 50 },
    'good': { color: '#f1c40f', text: 'Good', percentage: 70 },
    'strong': { color: '#2ecc71', text: 'Strong', percentage: 85 },
    'very-strong': { color: '#27ae60', text: 'Very Strong', percentage: 100 },
  };

  return feedback[strength] || feedback.none;
};

/**
 * Check if password contains common patterns (sequential, repeated, etc.)
 * 
 * @param {string} password - The password to check
 * @returns {Array<string>} - Array of warning messages
 */
export const checkPasswordPatterns = (password) => {
  const warnings = [];

  if (!password) return warnings;

  // Check for sequential characters
  if (/(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(password)) {
    warnings.push('Avoid sequential characters (abc, 123, etc.)');
  }

  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    warnings.push('Avoid repeated characters (aaa, 111, etc.)');
  }

  // Check for common keyboard patterns
  if (/(qwerty|asdfgh|zxcvbn|qazwsx)/i.test(password)) {
    warnings.push('Avoid keyboard patterns (qwerty, asdfgh, etc.)');
  }

  return warnings;
};

/**
 * Main validation function that checks everything
 * Useful for real-time validation as user types
 * 
 * @param {string} password - The password to validate
 * @returns {Object} - Complete validation result
 */
export const validatePasswordComplete = (password) => {
  const validation = validatePassword(password);
  const warnings = checkPasswordPatterns(password);
  const strengthFeedback = getPasswordStrengthFeedback(validation.strength);

  return {
    ...validation,
    warnings,
    strengthFeedback,
  };
};

export default validatePassword;

